import { makeStyles, mergeClasses } from '@griffel/react';

import {
  ClassNameAndInlineStyles,
  useAlignItemsClassName,
  useDisplayClassName,
  useFlexDirectionClassName,
  useFlexWrapClassName,
  useJustifyContentClassName,
} from '../../util/styles/fluent9';
import { getGutterValue } from '../../util/styles/gutters';
import { createTokens } from '../../util/theme/fluent9';

import { FlexProps } from './Flex.types';
import { useClassNameAndInlineStyles as useFlexItemClassNameAndInlineStyles } from './FlexItem.styles';

const { tokens, cssVariables } = createTokens(['columnGap', 'rowGap'], 'flex');

type ClassNameProps = Exclude<FlexProps, 'children' | 'ariaHidden' | 'role'>;
type UseClassNamesAndInlineStyles = (props: ClassNameProps) => ClassNameAndInlineStyles;

export const useClassNameAndInlineStyles: UseClassNamesAndInlineStyles = ({
  alignItems,
  direction,
  display = 'flex',
  justifyContent,
  wrap,

  gap: gapProp,
  columnGap: columnGapProp,
  rowGap: rowGapProp,
  className,

  ...flexItemStyleProps
}) => {
  const classNames = useFlexClassNames();
  const flexItemStyling = useFlexItemClassNameAndInlineStyles(flexItemStyleProps);
  const columnGap = columnGapProp != null ? columnGapProp : gapProp;
  const rowGap = rowGapProp != null ? rowGapProp : gapProp;
  const hasColumnGap = columnGap != null;
  const hasRowGap = rowGap != null;

  return {
    className: mergeClasses(
      classNames.root,
      flexItemStyling.className,
      useAlignItemsClassName(alignItems),
      useFlexDirectionClassName(direction),
      useDisplayClassName(display),
      useJustifyContentClassName(justifyContent),
      useFlexWrapClassName(wrap),
      hasColumnGap && classNames.columnGap,
      hasRowGap && classNames.rowGap,
      className
    ),
    inlineStyles: {
      ...flexItemStyling.inlineStyles,
      [cssVariables.columnGap]: hasColumnGap && getGutterValue(columnGap),
      [cssVariables.rowGap]: hasRowGap && getGutterValue(rowGap),
    },
  };
};

const useFlexClassNames = makeStyles({
  root: {
    /**
     * Allows text ellipsis to work for all children of <Flex>, even if not wrapped in a <FlexItem> component.
     * https://css-tricks.com/flexbox-truncated-text/
     */
    '> *': {
      minWidth: 0,
    },
  },
  columnGap: {
    columnGap: tokens.columnGap,
  },
  rowGap: {
    rowGap: tokens.rowGap,
  },
});
